import { type FC, useState } from "react";
import { Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreateProductMutation } from "@/partner/entities/products";
import type {
  CreateProductInputType,
  UpdateProductInputType,
} from "@/types/graphql/partner.generated";
import { ProductForm } from "./ProductForm";

interface CreateProductDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

/**
 * Диалог создания продукта (референс: CreateProductDialog.js) — сразу
 * fullScreen-форма, без мастера (enableCreationMasters не переносим).
 */
export const CreateProductDialog: FC<CreateProductDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // référence: Edit.js `{error && <ErrorMessage error={error} />}` — ошибка
  // мутации показывается ещё и персистентным алертом внутри формы (не только
  // снекбаром), пока пользователь не попробует сохранить снова.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createProduct, { loading }] = useCreateProductMutation({
    onCompleted: () => {
      setErrorMessage(null);
      enqueueSnackbar(t("products.create.success"), { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      setErrorMessage(error.message);
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["ProductsList"],
  });

  // ProductForm.onSubmit типизирован union'ом Create/UpdateProductInputType
  // (общий для обоих диалогов), но этот диалог не передаёт isEdit —
  // ProductForm всегда строит вход через buildProductInput
  // (CreateProductInputType, с `type`), так что каст здесь безопасен.
  const handleSubmit = async (
    input: CreateProductInputType | UpdateProductInputType
  ) => {
    await createProduct({
      variables: { input: input as CreateProductInputType },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen scroll="body">
      <ProductForm
        title={t("products.create.title")}
        loading={loading}
        error={errorMessage}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
